/**
 * Règles de rattachement des lignes du tableau des compteurs (Lot 9) à un groupe de monte.
 *
 * Isolées de `CompteursView.vue` pour être vérifiables : l'algorithme d'allocation y vit dans une
 * fonction locale de ~320 lignes fermée sur une dizaine de refs, non exportable en l'état.
 */
import type { CompteurNature } from '@/models/compteur';
import { concentrationCovers } from '@/helper/concentrationHelper';

/**
 * REQ887 : natures autorisées à faire APPARAÎTRE la colonne d'un schéma non sélectionné
 * (cf. `canOpenColumn` dans `CompteursView.vue`). Les autres natures ne font que renseigner
 * une colonne déjà ouverte par ailleurs.
 *
 * - `Besoin` / `Stock` / `Baseline` : PRÉVISIONNEL — une commande, du stock ou un plan de monte
 *   justifient à eux seuls d'afficher le schéma (au moins en « hors config »).
 * - `ParcPreleve` / `DispoAttente` : un PRÉLÈVEMENT réalisé mais pas encore orienté — la donnée
 *   existe et ne doit jamais disparaître silencieusement du tableau (bug rapporté 2026-08-31 :
 *   un prélèvement sur un schéma non configuré était invisible, y compris dans le total).
 * - `Dispo` (dispatch VALIDÉ/orienté) reste volontairement exclu : un simple dispatch orienté,
 *   seul, ne doit pas ouvrir de colonne (Mantis 0028247 — un unique verrat hétéro orienté ouvrait
 *   la colonne VAL sans qu'aucune commande/stock/plan de monte ne le justifie). Y ajouter `Dispo`
 *   réintroduirait ce bug.
 */
export const COLUMN_OPENING_NATURES: ReadonlySet<CompteurNature> = new Set<CompteurNature>([
    'Besoin',
    'Stock',
    'Baseline',
    'ParcPreleve',
    'DispoAttente',
]);

/**
 * Détermine dans quel pool verser un **Dispo sans verrat identifié**.
 *
 * Deux destinations possibles en amont des passes d'allocation :
 * - le **pool générique de la colonne** (`freeDispo`), que la 3ᵉ passe alloue aux besoins
 *   **en respectant leur groupe** (G1, G2…) ;
 * - le **seau sentinelle** (`dispoByBoar[-1]`, retourné ici par `null`), qu'aucun besoin ne
 *   réclame et qui part donc directement en « Hors Groupe ».
 *
 * Un lot hétérospermie est un blend : la projection lui pose `BoarId = null`, et les commandes
 * d'un groupe sont génériques (ni volume ni concentration) — le rapprochement « tier 3 » ne peut
 * donc jamais matcher. Le dispo hétéro finissait ainsi systématiquement en HG, alors même qu'un
 * besoin de groupe l'attendait (Mantis 0028305 / 0028306).
 *
 * Arbitrage MOE : « il ne doit pas y avoir de Hors Groupe pour l'hétéro, les doses orientées
 * doivent l'être dans un groupe ». On verse donc le dispo hétéro au pool générique de sa colonne.
 * Le bloc mono, lui, garde son comportement : un verrat sans commande propre n'a pas de groupe
 * de commande à rejoindre, et son groupe de monte est traité ailleurs (2ᵉ passe).
 *
 * @param tier3Key clé de colonne d'une commande sans verrat compatible (volume ET concentration), si trouvée
 * @param isHeteroSegment true pour les segments HeteroStandard / HeteroArmada
 * @param genericColKey clé de la colonne générique du schéma
 * @returns la clé du pool générique à alimenter, ou null pour le seau sentinelle (→ HG)
 */
export function loosePoolKeyForDispo(
    tier3Key: string | null,
    isHeteroSegment: boolean,
    genericColKey: string,
    servesLooseGenericBesoin = false,
): string | null {
    if (tier3Key) return tier3Key;
    return (isHeteroSegment || servesLooseGenericBesoin) ? genericColKey : null;
}

/**
 * Rapprochement volume/concentration d'un Dispo/Stock avec les commandes (Besoin) candidates
 * (décision produit 2026-07-27) : le VOLUME doit matcher EXACTEMENT, mais la concentration
 * réellement obtenue peut être supérieure ou égale à celle demandée — jamais inférieure (une dose
 * moins concentrée que demandé ne peut pas couvrir la commande). S'il existe plusieurs commandes
 * au même volume avec des concentrations différentes, on retient celle dont le seuil est le plus
 * ÉLEVÉ parmi ceux que le Dispo/Stock satisfait encore (le « fit » le plus proche par le dessous),
 * pour ne pas affecter une dose juste-suffisante à une commande moins exigeante alors qu'une
 * commande plus proche existe.
 *
 * Mantis 0028524 — une commande qui impose un VOLUME mais AUCUNE concentration
 * (`concentration = null`, cas de `dose_conc` à 0 côté Ogicom) était purement et simplement
 * écartée des candidates. Elle ouvrait bien sa colonne (« VAL 85 »), mais aucune dose ne pouvait
 * jamais l'y rejoindre : les doses produites retombaient dans la colonne générique du schéma et
 * la commande restait éternellement non couverte — « la commande sera toujours active, les doses
 * apparaîtront dans une autre colonne ». Qui n'exige rien accepte tout : une telle commande est
 * désormais couverte par n'importe quelle concentration à SON volume, mais elle passe APRÈS
 * toute commande qui a, elle, exigé une concentration précise — sinon une dose juste-suffisante
 * irait au moins exigeant alors qu'une commande plus proche l'attend.
 *
 * Mantis 0028534 — la comparaison se fait au centième (`concentrationCovers`) : la commande porte
 * spz / volume non arrondi (29.4118), la production la concentration proposée arrondie (29.41).
 *
 * Retourne `null` si aucune candidate ne convient.
 */
export function bestFitConcentrationKey(
    candidates: Iterable<{ key: string; volume: number | null; concentration: number | null }>,
    volume: number | null,
    concentration: number | null,
): string | null {
    if (volume == null || concentration == null) return null;

    let best: { concentration: number; key: string } | null = null;
    let volumeOnly: string | null = null;

    for (const cand of candidates) {
        if (cand.volume !== volume) continue;
        if (cand.concentration == null) {
            volumeOnly ??= cand.key;
            continue;
        }
        if (!concentrationCovers(concentration, cand.concentration)) continue;
        if (!best || cand.concentration > best.concentration) best = { concentration: cand.concentration, key: cand.key };
    }

    return best?.key ?? volumeOnly;
}

/**
 * Un Dispo/Stock peut-il couvrir une commande qui n'a EXIGÉ aucun volume (« générique ») ?
 *
 * Arbitrage du 22/09/2026, aligné sur la grille d'orientation : une commande sans volume se sert
 * au volume PAR DÉFAUT du centre (option `VOLUME_STANDARD`). Une dose d'un autre volume ne la
 * couvre donc pas — elle reste affichée dans la même colonne, en production libre (« Hors
 * Groupe »), au lieu de solder une commande qu'elle ne sert pas.
 *
 * La règle corrige deux écarts opposés, hérités du fait que le volume n'entrait dans le
 * rapprochement que par la paire (volume, concentration) d'une commande SPÉCIFIQUE :
 * - une commande générique portée par un VERRAT était couverte par n'importe quelle dose de ce
 *   verrat, quel que soit son volume ;
 * - une commande générique SANS verrat — le cas courant, Ogicom laissant `dose_vol` à 0 — n'était
 *   JAMAIS couverte, même par des doses au volume par défaut : le compteur affichait un besoin
 *   non couvert que l'écran d'orientation, lui, montrait soldé par le stock (commande T-000010,
 *   40 doses DDP, recette du 22/09/2026).
 *
 * `defaultVolume` inexploitable (option absente ou littéralement « NaN », il en existe en base) :
 * on ne substitue aucune valeur et on rapproche sans condition de volume, plutôt que de ne plus
 * rien rapprocher du tout. Le défaut de paramétrage est signalé par `getNumericOptionByCode`.
 */
export function dispoCoversGenericBesoin(
    dispoVolume: number | null | undefined,
    defaultVolume: number | null | undefined,
): boolean {
    const def = Number(defaultVolume);
    if (!Number.isFinite(def) || def <= 0) return true;
    return Number(dispoVolume) === def;
}

/**
 * Groupe d'accueil de l'**excédent** hétéro d'une colonne : les doses orientées que les besoins
 * de groupe ne reprennent pas.
 *
 * Arbitrage MOE (Mantis 0028305 / 0028306) : « l'excédent doit se trouver dans le premier groupe
 * des besoins » — si G1 porte un besoin, l'excédent va en G1 ; si seul G2 en porte un, il va en
 * G2. Le remplissage ordonné (G1 puis G2, puis l'excédent) est assuré en amont par la passe
 * d'allocation, qui parcourt les besoins triés par groupe.
 *
 * Un besoin classé « Hors Groupe » (commande sans créneau) n'est pas un groupe d'accueil valable :
 * il est ignoré, sinon l'excédent ressortirait hors plan de monte — précisément ce que l'arbitrage
 * proscrit.
 *
 * @param besoinsSortedByGroup besoins, DÉJÀ triés par ordre de groupe (cf. compareGroupKeys)
 * @param colKey colonne concernée
 * @param hgKey clé « Hors Groupe »
 * @param fallbackGroupKey groupe de repli quand la colonne ne porte aucun besoin groupé
 */
export function excessGroupKey(
    besoinsSortedByGroup: { colKey: string; groupKey: string }[],
    colKey: string,
    hgKey: string,
    fallbackGroupKey: string,
): string {
    const premier = besoinsSortedByGroup.find((b) => b.colKey === colKey && b.groupKey !== hgKey);
    return premier ? premier.groupKey : fallbackGroupKey;
}

/** Ordre G1 < G2 < ... (indices de gabarit, numériques) ; fallback alphabétique pour le
 * legacy (créneaux bruts "08:00", "10:00"...) — jamais mélangés dans un même jeu de données. */
export function compareGroupKeys(a: string, b: string): number {
    const na = Number(a), nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.localeCompare(b);
}

/** Besoin d'un verrat sur une colonne, tel que le rapprochement le manipule. */
export interface BoarBesoinPart {
    qty: number;
    groupKey: string;
    isReserved: boolean;
}

/**
 * Range un besoin d'un verrat dans la liste de SA colonne (schéma / volume / concentration).
 *
 * Deux commandes du MÊME verrat sur la MÊME colonne ne fusionnent que si elles sont aussi du même
 * groupe et du même statut réservé. Elles fusionnaient toujours : la quantité s'additionnait mais
 * seul le groupe de la première était gardé — deux commandes de HMXHO37 en 85 ml / 29,42 à 08:00 et
 * 11:00 s'affichaient 80 doses en G1, rien en G2 (question de Quentin, recette du 30/09/2026).
 */
export function addBoarBesoinPart<T extends BoarBesoinPart>(parts: T[], part: T): void {
    const meme = parts.find((p) => p.groupKey === part.groupKey && p.isReserved === part.isReserved);
    if (meme) meme.qty += part.qty;
    else parts.push(part);
}

/**
 * Couverture des besoins d'UN verrat sur UNE colonne par l'offre (dispo + stock) de ce verrat sur
 * cette colonne, groupe par groupe dans l'ordre (G1 puis G2...) : l'offre sert d'abord la commande
 * la plus tôt. Dans l'offre, le standard est consommé avant le réservé (règle existante).
 *
 * Le reliquat est rendu avec le groupe de la DERNIÈRE commande couverte : un surplus ne reporte que
 * dans son propre groupe (3e passe), et c'est celui de la commande la plus tardive qui l'a laissé.
 */
export function coverBoarBesoinsInGroupOrder<T extends BoarBesoinPart>(
    parts: T[],
    offre: { standard: number; reserved: number },
): { coverage: { part: T; covered: number }[]; remainder: { standard: number; reserved: number }; remainderGroupKey: string | null } {
    const ordered = [...parts].sort((a, b) => compareGroupKeys(a.groupKey, b.groupKey));
    let standard = offre.standard;
    let reserved = offre.reserved;
    const coverage = ordered.map((part) => {
        const covered = Math.min(part.qty, standard + reserved);
        const fromStandard = Math.min(covered, standard);
        standard -= fromStandard;
        reserved -= covered - fromStandard;
        return { part, covered };
    });
    const last = ordered[ordered.length - 1];
    return { coverage, remainder: { standard, reserved }, remainderGroupKey: last ? last.groupKey : null };
}

/**
 * Pools de surplus d'AUTRES colonnes qui peuvent couvrir une commande générique (sans volume exigé)
 * restée non couverte après la 3e passe (Mantis 0028612).
 *
 * Un surplus ne reportait que dans SA colonne. Or une commande générique se sert au volume par
 * défaut du centre (arbitrage du 22/09/2026, cf. dispoCoversGenericBesoin) : une dose à ce volume
 * rangée dans une colonne « volume seul » du même schéma — parce que son verrat y porte une
 * commande nominative — pouvait la servir. Recette STE du 07/10 : VALG210 10 Selfix 85 ml couverte
 * par la 1re orientation de G210, la 2e restait en surplus (+10) dans « VAL 85 » pendant que la
 * commande VAL sans volume restait à -10.
 *
 * Retenus : même schéma, même groupe, colonne SANS concentration exigée (une dose rangée dans une
 * colonne à concentration la sert en priorité ; son excédent reste dans sa colonne) et dont le
 * volume couvre la commande générique.
 *
 * Statut : une commande réservée ne prend que du surplus réservé. Une commande standard prend le
 * surplus standard, PUIS le surplus réservé : ce statut vient de la réservation active du verrat
 * (CompteurProjectionService.IsBoarReservedAsync), pas de sa commande nominative, et rien ne retient
 * ce qui dépasse ses commandes — arbitrages 0028612 et 0028587, il couvre le classique. Sans cela,
 * 0028612 n'était pas tenu dès que le verrat à commande nominative était aussi réservé.
 *
 * Une commande qui exige une concentration n'en prend aucun : la colonne « volume seul » d'où vient
 * le surplus ne dit rien de la concentration de ses doses.
 *
 * @param poolKeys clés `${schemaId}__${vol}__${conc}|${groupKey}|${statut}` du surplus
 * @returns clés retenues, celles du surplus standard d'abord
 */
export function genericBesoinSurplusPoolKeys(
    poolKeys: Iterable<string>,
    besoin: { schemaId: number; groupKey: string; isReserved: boolean; concentration?: number | null },
    defaultVolume: number | null | undefined,
): string[] {
    if (besoin.concentration != null) return [];
    const statuses = besoin.isReserved ? ['reserved'] : ['standard', 'reserved'];
    const keys: string[] = [];
    for (const poolKey of poolKeys) {
        const [colKey, groupKey, poolStatus] = poolKey.split('|');
        // La colonne générique du schéma (`${schemaId}____`) est écartée par `vol === ''` ci-dessous.
        if (groupKey !== besoin.groupKey || !statuses.includes(poolStatus)) continue;
        const [schemaId, vol, conc] = colKey.split('__');
        if (schemaId !== String(besoin.schemaId) || vol === '' || conc !== '') continue;
        if (!dispoCoversGenericBesoin(Number(vol), defaultVolume)) continue;
        keys.push(poolKey);
    }
    return keys.sort((a, b) => statuses.indexOf(a.split('|')[2]) - statuses.indexOf(b.split('|')[2]));
}

/**
 * 3e passe bis du compteur (Mantis 0028612) : une commande générique (sans volume exigé) encore
 * non couverte après la 3e passe prend le surplus des colonnes « volume seul » du même schéma et
 * du même groupe dont le volume la sert (cf. genericBesoinSurplusPoolKeys).
 *
 * Dans un groupe, les commandes réservées passent AVANT les standard (revue MR !1095) : une
 * réservée ne peut prendre que le surplus réservé, alors qu'une standard prend le standard PUIS
 * le réservé. Servie la première, une standard pouvait vider le seul pool d'une réservée du même
 * groupe. Les pools étant propres à un groupe, l'ordre entre groupes est indifférent.
 *
 * @param candidates commandes sans verrat
 * @param uncovered reste à couvrir de chaque commande après la 3e passe
 * @param surplus pools de surplus, DÉCRÉMENTÉS de ce qui est pris (le reliquat part en 4e passe)
 * @returns ce que chaque commande prend, dans l'ordre des prises
 */
export function allocateGenericBesoinSurplus<T extends { schemaId: number; groupKey: string; isReserved: boolean; volume: number | null; concentration?: number | null }>(
    candidates: T[],
    uncovered: Map<T, number>,
    surplus: Map<string, number>,
    defaultVolume: number | null | undefined,
): { entry: T; taken: number }[] {
    const allocations: { entry: T; taken: number }[] = [];
    const ordered = [...candidates].sort((a, b) =>
        compareGroupKeys(a.groupKey, b.groupKey) || Number(b.isReserved) - Number(a.isReserved));
    for (const entry of ordered) {
        let missing = uncovered.get(entry) ?? 0;
        if (missing <= 0 || entry.volume != null) continue;
        for (const poolKey of genericBesoinSurplusPoolKeys(surplus.keys(), entry, defaultVolume)) {
            const taken = Math.min(missing, surplus.get(poolKey) ?? 0);
            if (taken <= 0) continue;
            surplus.set(poolKey, (surplus.get(poolKey) ?? 0) - taken);
            allocations.push({ entry, taken });
            missing -= taken;
            if (missing <= 0) break;
        }
    }
    return allocations;
}

/** Apport d'un verrat (ou du Dispo sans verrat) au « dispo libre » d'une colonne, avec le statut
 * (réservé / standard) sous lequel il a été versé : la 3e passe consomme chaque statut à part. */
export interface FreeDispoSource {
    boarId: number;
    qty: number;
    isReserved: boolean;
}

/**
 * « Dispo libre » d'une colonne : production de verrats SANS commande à leur nom, et Dispo sans
 * verrat, qui peut couvrir une commande sans verrat (3e passe). Les apports sont gardés par verrat
 * pour que le reliquat retombe dans le groupe de monte de CHAQUE verrat (Mantis 0028587), au lieu
 * de partir en bloc en HG.
 */
export interface FreeDispoBucket {
    reserved: number;
    standard: number;
    sources: FreeDispoSource[];
}

/** « Dispo libre » vide d'une colonne qui n'en a pas encore reçu. */
export function emptyFreeDispo(): FreeDispoBucket {
    return { reserved: 0, standard: 0, sources: [] };
}

/**
 * Ajoute l'apport d'un verrat au dispo libre.
 *
 * Arbitrage du 12/10/2026 (Mantis 0028587) : la production d'un verrat RÉSERVÉ qui n'a aucune
 * commande à son nom couvre les commandes classiques, comme le surplus d'un verrat à commande
 * nominative (arbitrage 0028612). Rien ne la retient : elle est donc versée au standard. Elle était
 * gardée au réservé, que seule une commande réservée consomme — or une commande sans verrat n'est
 * jamais réservée : la production partait toujours en HG (TLT 2640, recette STE du 01/10).
 * `holdReserved` reste pour le Dispo sans verrat, dont le statut n'a pas changé.
 */
export function addFreeDispo(bucket: FreeDispoBucket, boarId: number, qty: number, isReserved: boolean, holdReserved: boolean): void {
    const held = isReserved && holdReserved;
    if (held) bucket.reserved += qty;
    else bucket.standard += qty;
    const source = bucket.sources.find((s) => s.boarId === boarId && s.isReserved === held);
    if (source) source.qty += qty;
    else bucket.sources.push({ boarId, qty, isReserved: held });
}

/**
 * Retire `qty` des apports du statut servi (`isReserved`), dans leur ordre d'arrivée : ce que la
 * 3e passe a consommé dans le dispo libre. L'ordre d'arrivée est celui des données, sans préférence
 * de groupe — la 3e passe ne sait pas de quel verrat vient chaque dose, il faut en choisir un, et ce
 * choix est stable. Le statut, lui, est connu : une commande standard ne consomme que du standard.
 * Consommer sans le distinguer laissait le reliquat sur les apports du mauvais statut, donc
 * potentiellement dans le groupe de monte d'un autre verrat.
 */
export function consumeFreeDispoSources(sources: FreeDispoSource[], qty: number, isReserved: boolean): void {
    let reste = qty;
    for (const source of sources) {
        if (reste <= 0) break;
        if (source.isReserved !== isReserved) continue;
        const pris = Math.min(source.qty, reste);
        source.qty -= pris;
        reste -= pris;
    }
}

/**
 * Reliquat du dispo libre, ventilé par groupe : le groupe de monte de chaque verrat, HG à défaut
 * (verrat hors plan de monte, ou Dispo sans verrat) — la règle de la production non liée à une
 * commande (Mantis 0028296), qui ne s'appliquait pas ici.
 */
export function freeDispoLeftoverByGroup(
    sources: FreeDispoSource[],
    groupKeyOfBoar: (boarId: number) => string | undefined,
    hgKey: string,
): Map<string, number> {
    const byGroup = new Map<string, number>();
    for (const source of sources) {
        if (source.qty <= 0) continue;
        const groupKey = groupKeyOfBoar(source.boarId) ?? hgKey;
        byGroup.set(groupKey, (byGroup.get(groupKey) ?? 0) + source.qty);
    }
    return byGroup;
}
